import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Frame, X } from "lucide-react";
import { api, photoSrc } from "@/lib/api";
import { CATEGORIES, TYPE_LABELS } from "@/data/content";

const FILTER_IDS = {
  alle: "gallery-filter-all",
  events: "gallery-filter-events",
  hochzeit: "gallery-filter-hochzeit",
  unternehmen: "gallery-filter-unternehmen",
  landschaft: "gallery-filter-landschaft",
  tiere: "gallery-filter-tiere",
  automotive: "gallery-filter-automotive",
};

export const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [lightbox, setLightbox] = useState(null);
  const [framed, setFramed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/photos")
      .then((res) => setPhotos(res.data))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = filter === "alle" ? photos : photos.filter((p) => p.category === filter);

  const step = useCallback(
    (dir) => {
      setLightbox((cur) => (cur === null ? null : (cur + dir + visible.length) % visible.length));
    },
    [visible.length]
  );

  const stepManual = useCallback(
    (dir) => {
      step(dir);
    },
    [step]
  );

  const openLightbox = (idx) => {
    setFramed(false);
    setLightbox(idx);
  };

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") stepManual(1);
      if (e.key === "ArrowLeft") stepManual(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, stepManual]);

  useEffect(() => {
    if (lightbox === null) return;
    const t = setInterval(() => step(1), 5000);
    return () => clearInterval(t);
  }, [lightbox, step]);

  const active = lightbox !== null ? visible[lightbox] : null;

  return (
    <section id="portfolio" data-testid="gallery-section" className="mx-auto max-w-[1440px] px-6 py-28 lg:px-12 lg:py-40">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="mb-14 flex flex-col justify-between gap-8 lg:flex-row lg:items-end"
      >
        <div>
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.35em] text-gold">Portfolio — Ausgewählte Arbeiten</p>
          <h2 className="font-serif text-3xl font-light tracking-tight text-white sm:text-4xl lg:text-5xl">
            Licht, das Geschichten <span className="italic text-zinc-400">erzählt.</span>
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-zinc-500">
          Jede Serie ist ein eigenes Kapitel. Filtern Sie nach Genre — oder lassen Sie sich treiben.
        </p>
      </motion.div>

      <div className="mb-12 flex flex-wrap gap-x-8 gap-y-3 border-b border-white/10 pb-5" data-testid="gallery-filters">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={FILTER_IDS[c.key]}
            onClick={() => {
              setFilter(c.key);
              setLightbox(null);
            }}
            className={`relative pb-1 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${
              filter === c.key ? "text-gold" : "text-zinc-500 hover:text-white"
            }`}
          >
            {c.label}
            {filter === c.key && (
              <motion.span layoutId="filter-underline" className="absolute -bottom-[21px] left-0 right-0 h-px bg-gold" />
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center" data-testid="gallery-loading">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600">Galerie wird geladen …</p>
        </div>
      ) : (
        <motion.div layout className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-x-8 lg:gap-y-16">
          <AnimatePresence mode="popLayout">
            {visible.map((photo, idx) => {
              const spans = [
                "lg:col-span-8",
                "lg:col-span-4 lg:mt-24",
                "lg:col-span-4 lg:-mt-8",
                "lg:col-span-8 lg:mt-16",
                "lg:col-span-7",
                "lg:col-span-5 lg:mt-28",
              ];
              return (
                <motion.figure
                  layout
                  key={photo.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className={`group relative ${spans[idx % 6]}`}
                >
                  <button
                    data-testid="gallery-item-card"
                    data-cursor="view"
                    onClick={() => openLightbox(idx)}
                    className="block w-full cursor-pointer text-left"
                  >
                    <div className={`${photo.aspect || "aspect-[4/5]"} w-full overflow-hidden bg-ink3`}>
                      <img
                        src={photoSrc(photo)}
                        alt={photo.title}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>
                    <div className="pointer-events-none absolute inset-0 flex flex-col justify-between bg-gradient-to-t from-ink/80 via-transparent to-ink/30 p-5 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold">
                          {TYPE_LABELS[photo.category] || photo.category}
                        </span>
                        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-300">
                          № {String(idx + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="flex items-end justify-between">
                        <p className="font-serif text-lg italic text-white">{photo.title}</p>
                        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-400">Ulm · f/1.8</span>
                      </div>
                    </div>
                  </button>
                </motion.figure>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="lightbox-modal-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 p-4 backdrop-blur-md lg:p-10"
            onClick={() => setLightbox(null)}
          >
            <button
              data-testid="lightbox-close-button"
              onClick={() => setLightbox(null)}
              className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-gold hover:text-gold"
              aria-label="Schließen"
            >
              <X size={18} />
            </button>
            <button
              data-testid="lightbox-frame-button"
              onClick={(e) => {
                e.stopPropagation();
                setFramed((f) => !f);
              }}
              className={`absolute right-20 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border transition-colors ${
                framed ? "border-gold bg-gold text-ink" : "border-white/20 text-white hover:border-gold hover:text-gold"
              }`}
              aria-label="Im Rahmen ansehen"
              title="So könnte es bei dir hängen"
            >
              <Frame size={18} />
            </button>
            <button
              data-testid="lightbox-prev-button"
              onClick={(e) => {
                e.stopPropagation();
                stepManual(-1);
              }}
              className="absolute left-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-gold hover:text-gold"
              aria-label="Vorheriges Bild"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              data-testid="lightbox-next-button"
              onClick={(e) => {
                e.stopPropagation();
                stepManual(1);
              }}
              className="absolute right-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-gold hover:text-gold"
              aria-label="Nächstes Bild"
            >
              <ArrowRight size={18} />
            </button>

            <motion.div
              key={`${active.id}-${framed}`}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="flex max-h-full w-full max-w-6xl flex-col items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {framed ? (
                <div
                  className="flex w-full flex-col items-center justify-center px-6 py-10"
                  data-testid="lightbox-frame-preview"
                  style={{
                    background:
                      "radial-gradient(ellipse at 50% 0%, #4a453e 0%, #35312c 45%, #221f1b 100%)",
                    minHeight: "70vh",
                  }}
                >
                  <div className="border-[10px] border-[#141210] bg-[#141210] shadow-[0_40px_80px_rgba(0,0,0,0.7)]">
                    <div className="bg-[#f2eee4] p-6 sm:p-10">
                      <img
                        src={photoSrc(active)}
                        alt={active.title}
                        decoding="async"
                        className="max-h-[48vh] w-auto max-w-full object-contain"
                        data-testid="lightbox-image"
                      />
                    </div>
                  </div>
                  <p className="mt-8 font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-400">
                    So könnte es bei dir hängen — Fine-Art-Druck mit Rahmen
                  </p>
                </div>
              ) : (
                <img
                  src={photoSrc(active)}
                  alt={active.title}
                  decoding="async"
                  className="max-h-[85vh] w-auto max-w-full object-contain"
                  data-testid="lightbox-image"
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
